import * as React from 'react';
import type { InPageNavigationServiceV1 } from '@volkswagen-onehub/audi-in-page-navigation-service';
import { useMemo } from 'react';
import type { NavigationBarItem } from '@oneaudi/unified-web-components/dist/cjs/components/navigation-bar';
import { useInViewObservers } from '@oneaudi/feature-app-utils';
import { StyledContainer, StyledNavigationBar } from './AnchorNavigationStyles';
import { WarningMessage } from '../warningmessage/WarningMessage';
import { useInPageNavigationService } from '../../context/InPageNavigationContext';
import type { AnchorNavigationProps, RegisteredAnchor } from './AnchorNavigationTypes';
import { useTrackingManager } from '../../utils/useTrackingManager';
import { useI18n } from '../../i18n';

const toRegisteredAnchor = (element: HTMLElement): RegisteredAnchor => ({
  element,
  anchor: {
    id: element.id,
    name: String(element.getAttribute('data-anchor-name')),
  },
});

const findRegisteredServiceAnchors = (inPageNavigationService: InPageNavigationServiceV1) =>
  inPageNavigationService
    .getOrderedAnchorsList()
    .map(({ id }) => document.getElementById(id))
    .filter(notEmpty)
    .map(toRegisteredAnchor);

function notEmpty<TValue>(value: TValue | null | undefined): value is TValue {
  return value !== null && value !== undefined;
}

const minAnchors = 2;
const maxAnchors = 10;

export const AnchorNavigation: React.FC<AnchorNavigationProps> = ({ linkTracking }) => {
  const inPageNavigationService = useInPageNavigationService();
  const [registeredAnchors, setRegisteredAnchors] = React.useState<RegisteredAnchor[]>();

  const navigationRef = React.useRef<HTMLDivElement>(null);
  const activeAnchorRef = React.useRef<HTMLAnchorElement>(null);

  const [inView, setInView] = React.useReducer(() => true, false);
  const trackingManager = useTrackingManager();
  const [observeInView, disconnectInView] = useInViewObservers(setInView);

  React.useEffect(() => {
    if (inView) {
      disconnectInView();
    }
    return () => {
      disconnectInView();
    };
  }, [inView]);

  let hasTracked = false;

  const initObservers = React.useCallback((container: HTMLDivElement) => {
    observeInView(container);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!hasTracked && entry.isIntersecting && entry.intersectionRatio >= 0.7) {
            trackingManager.impression();
            hasTracked = true;
            setInView();
          }
        });
      },
      {
        threshold: 0.7,
      },
    );
    observer.observe(container);
  }, []);

  const [activeAnchor, setActiveAnchor] = React.useState(
    registeredAnchors?.findIndex(({ anchor: { id } }) => id === window.location.hash) ?? -1,
  );

  const handleAnchorFallback = React.useCallback(() => {
    const serviceAnchors = findRegisteredServiceAnchors(inPageNavigationService);
    if (!registeredAnchors && !serviceAnchors) return;

    if (!registeredAnchors || registeredAnchors.length !== serviceAnchors.length) {
      setRegisteredAnchors(serviceAnchors);
    } else {
      const anchorJSON = JSON.stringify(serviceAnchors.map(({ anchor }) => anchor));
      const registeredAnchorJSON = JSON.stringify(registeredAnchors.map(({ anchor }) => anchor));
      if (anchorJSON !== registeredAnchorJSON) {
        setRegisteredAnchors(registeredAnchors);
      }
    }
  }, [registeredAnchors, inPageNavigationService]);

  React.useEffect(() => {
    let checkInterval: number | undefined;

    const checkForAnchors = () => {
      const anchors = Array.from(document.querySelectorAll<HTMLElement>('[data-anchor-name]'));

      if (anchors.length > 0) {
        setRegisteredAnchors(anchors.map(toRegisteredAnchor));
      } else {
        handleAnchorFallback();
        checkInterval = window.setTimeout(checkForAnchors, 500);
      }
    };

    checkForAnchors();

    return () => {
      if (checkInterval !== undefined) {
        clearTimeout(checkInterval);
      }
    };
  }, []);

  const anchorContentMap = useMemo(() => {
    if (!registeredAnchors || registeredAnchors.length === 0) return [];

    return registeredAnchors
      .map((anchor, index) => {
        const anchorElement = document.getElementById(anchor.anchor.id);

        if (!anchorElement) return null;

        return {
          index,
          element: anchorElement,
        };
      })
      .filter(
        (item): item is { index: number; element: HTMLElement } =>
          item !== null && item.element !== null,
      );
  }, [registeredAnchors]);

  React.useEffect(() => {
    if (!registeredAnchors || registeredAnchors.length === 0) return;

    const observerOptions = {
      root: null,
      rootMargin: `0% 0% -70% 0%`,
      threshold: 0.7,
    };

    const handleIntersection = (entries: IntersectionObserverEntry[]) => {
      let topPriorityAnchor: number | null = null;

      entries.forEach((entry) => {
        const targetElement = entry.target as HTMLElement;

        if (entry.isIntersecting) {
          const anchor = anchorContentMap.find((item) => item.element === targetElement);
          if (anchor) {
            topPriorityAnchor = anchor.index;
          }
        }
      });

      // Only update activeAnchor if it has changed
      if (topPriorityAnchor !== null && topPriorityAnchor !== activeAnchor) {
        setActiveAnchor(topPriorityAnchor);
      }
    };

    const observer = new IntersectionObserver(handleIntersection, observerOptions);

    anchorContentMap.forEach(({ element }) => {
      if (element) observer.observe(element);
    });
    /* eslint-disable consistent-return */

    return () => {
      anchorContentMap.forEach(({ element }) => {
        if (element) observer.unobserve(element);
      });
    };
  }, [registeredAnchors, activeAnchor]);

  if (!registeredAnchors) {
    return null;
  }

  const handleContentClick = (name: string, index: number) => {
    setActiveAnchor(index); // highlight clicked anchor
    if (linkTracking) {
      linkTracking(name, index + 1);
    }
  };

  const items: NavigationBarItem[] = registeredAnchors.map(
    (element: RegisteredAnchor, index: number) => ({
      label: element.anchor.name,
      active: activeAnchor === index,
      href: `#${element.anchor.id}`,
      ref: activeAnchor !== null && activeAnchor === index ? activeAnchorRef : undefined,
      onClick: () => {
        handleContentClick(element.anchor.name, index);
      },
      'aria-label': element.anchor.name,
    }),
  );

  const render = () => {
    if (registeredAnchors.length < minAnchors) {
      return null;
    }
    return (
      <StyledContainer
        as="div"
        data-testid="anchor-navigation"
        ref={initObservers}
        role="navigation"
      >
        <StyledNavigationBar
          ref={navigationRef}
          scrollButtons={{
            left: {
              ariaLabel: useI18n('leftScrollButton'),
            },
            right: {
              ariaLabel: useI18n('rightScrollButton'),
            },
          }}
          id="navigation-bar-id"
          size={process.env.IS_LEGACY ? 'md' : 'sm'}
          items={items}
          aria-label="Anchor"
        />
      </StyledContainer>
    );
  };

  return (
    <>
      <WarningMessage
        anchors={registeredAnchors.length}
        maxAnchors={maxAnchors}
        minAnchors={minAnchors}
      />
      {render()}
    </>
  );
};
